import React, { useState } from 'react';
import { generateBrief, writeContent, regenerateMeta, exportContentDoc } from '../../utils/api';
import { supabase } from '../../lib/supabase';

const STAGE_COLORS = {
  'Attention': { bg: '#E6F1FB', color: '#0C447C', border: '#85B7EB' },
  'Interest':  { bg: '#EAF3DE', color: '#27500A', border: '#97C459' },
  'Desire':    { bg: '#FAEEDA', color: '#633806', border: '#FAC775' },
  'E-A-T':     { bg: '#EEEDFE', color: '#26215C', border: '#AFA9EC' },
  'Action':    { bg: '#FAECE7', color: '#4A1B0C', border: '#F0997B' },
  'Additional':{ bg: '#F1EFE8', color: '#2C2C2A', border: '#B4B2A9' },
};

function EatForm({ client, onSubmit, onBack }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone:               client.phone || '',
    email:               client.email || '',
    years_trading:       client.years_trading || '',
    team_size:           client.team_size || '',
    accreditations:      client.accreditations || '',
    awards:              client.awards || '',
    clients_served:      client.clients_served || '',
    stats:               client.stats || '',
    notable_clients:     client.notable_clients || '',
    unique_selling_points: client.unique_selling_points || '',
    pricing_info:          client.pricing_info || '',
    include_pricing:       client.include_pricing !== undefined ? client.include_pricing : true,
  });

  const update = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const handleSubmit = async () => {
    setSaving(true);
    // Save all fields back to the client profile
    await supabase
      .from('clients')
      .update({
        ...form,
        include_pricing: form.include_pricing,
        pricing_info: form.pricing_info,
      })
      .eq('id', client.id);
    setSaving(false);
    onSubmit(form);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Back</button>
        <div>
          <div className="page-title">Client information</div>
          <div className="page-sub">This information will be used to write more specific, credible content. All fields are optional.</div>
        </div>
      </div>

      {/* Contact details */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Contact details</div>
        <div className="card-sub">Used in calls to action throughout the content.</div>
        <div className="form-row form-row-2 form-group">
          <div>
            <label>Phone number</label>
            <input type="text" placeholder="e.g. 01737 773 768" value={form.phone} onChange={e => update('phone', e.target.value)} />
          </div>
          <div>
            <label>Email address</label>
            <input type="email" placeholder="e.g. info@business.co.uk" value={form.email} onChange={e => update('email', e.target.value)} />
          </div>
        </div>
      </div>

      {/* EAT signals */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Expertise, Authority & Trust signals</div>
        <div className="card-sub">The more detail you provide, the more specific and convincing the content will be.</div>

        <div className="form-row form-row-2 form-group">
          <div>
            <label>Years trading</label>
            <input type="text" placeholder="e.g. 15 years, since 2008" value={form.years_trading} onChange={e => update('years_trading', e.target.value)} />
          </div>
          <div>
            <label>Team size</label>
            <input type="text" placeholder="e.g. 12 full-time staff" value={form.team_size} onChange={e => update('team_size', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Accreditations & certifications</label>
          <input type="text" placeholder="e.g. Which? Trusted Trader, NCCA member, TrustMark approved" value={form.accreditations} onChange={e => update('accreditations', e.target.value)} />
        </div>

        <div className="form-group">
          <label>Awards & recognition</label>
          <input type="text" placeholder="e.g. Best Local Business 2023, Checkatrade Top Rated" value={form.awards} onChange={e => update('awards', e.target.value)} />
        </div>

        <div className="form-row form-row-2 form-group">
          <div>
            <label>Number of clients / jobs completed</label>
            <input type="text" placeholder="e.g. Over 2,000 happy customers" value={form.clients_served} onChange={e => update('clients_served', e.target.value)} />
          </div>
          <div>
            <label>Notable clients or well-known businesses served</label>
            <input type="text" placeholder="e.g. NHS, Tesco, local councils" value={form.notable_clients} onChange={e => update('notable_clients', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Key stats or data points</label>
          <textarea
            rows={2}
            placeholder="e.g. 98% customer satisfaction rate, average response time 2 hours, 500+ 5-star reviews on Google"
            value={form.stats}
            onChange={e => update('stats', e.target.value)}
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Unique selling points</label>
          <textarea
            rows={2}
            placeholder="e.g. Same-day service available, eco-friendly products only, 100% satisfaction guarantee, family run business"
            value={form.unique_selling_points}
            onChange={e => update('unique_selling_points', e.target.value)}
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Pricing</div>
        <div className="card-sub">Including price points can improve conversions for some businesses, but for others (e.g. bespoke services, high-value B2B) it's better not to mention them.</div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0, fontWeight: 400 }}>
            <input
              type="radio"
              name="include_pricing"
              checked={form.include_pricing === true}
              onChange={() => update('include_pricing', true)}
            />
            Include pricing information in the content
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0, fontWeight: 400 }}>
            <input
              type="radio"
              name="include_pricing"
              checked={form.include_pricing === false}
              onChange={() => update('include_pricing', false)}
            />
            Do not mention pricing
          </label>
        </div>

        {form.include_pricing && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Pricing details</label>
            <textarea
              rows={2}
              placeholder="e.g. Prices start from £25, free quotes available, competitive rates, no hidden fees"
              value={form.pricing_info}
              onChange={e => update('pricing_info', e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <><span className="spinner" /> Saving…</> : 'Save & generate briefs →'}
        </button>
      </div>
    </div>
  );
}

function BriefCard({ brief, client, eatInfo }) {
  const [expanded, setExpanded] = useState(true);
  const [writing, setWriting] = useState(false);
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [referenceUrl, setReferenceUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState(brief.metaTitle || '');
  const [metaDescription, setMetaDescription] = useState(brief.metaDescription || '');
  const [regenTitle, setRegenTitle] = useState(false);
  const [regenDesc, setRegenDesc] = useState(false);
  const [exporting, setExporting] = useState(false);
  const imageIdeas = brief.imageIdeas || [];

  const handleRegenTitle = async () => {
    setRegenTitle(true);
    try {
      const result = await regenerateMeta(brief.targetKeyword, client.name || client.businessName, metaTitle, metaDescription, 'title');
      if (result.metaTitle) setMetaTitle(result.metaTitle);
    } catch (e) { alert('Failed to regenerate title tag.'); }
    setRegenTitle(false);
  };

  const handleRegenDesc = async () => {
    setRegenDesc(true);
    try {
      const result = await regenerateMeta(brief.targetKeyword, client.name || client.businessName, metaTitle, metaDescription, 'description');
      if (result.metaDescription) setMetaDescription(result.metaDescription);
    } catch (e) { alert('Failed to regenerate meta description.'); }
    setRegenDesc(false);
  };

  const handleExportDoc = async () => {
    if (!content) return;
    setExporting(true);
    try {
      await exportContentDoc(content, metaTitle, metaDescription, brief.targetKeyword, client.name || client.businessName, imageIdeas);
    } catch (e) {
      alert('Failed to export document — please try again.');
    }
    setExporting(false);
  };

  const handleWrite = async () => {
    setWriting(true);
    try {
      const result = await writeContent(
        { ...brief, eatInfo },
        client.businessName || client.name,
        client.description,
        referenceUrl
      );
      setContent(result.content);
      setEditing(true);
    } catch (e) {
      alert('Failed to write content — please try again.');
    }
    setWriting(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: expanded ? 16 : 0 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 4 }}>{brief.pageType}</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 4 }}>{brief.targetKeyword}</div>
          <div style={{ fontSize: 13, color: 'var(--blue-mid)' }}>{brief.suggestedH1}</div>
        </div>
        <button className="btn btn-sm" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {expanded && (
        <>
          {/* Meta */}
          <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
            {/* Title tag */}
            <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', padding: '10px 12px', border: `1px solid ${metaTitle.length > 60 ? 'var(--red)' : 'var(--gray-200)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Title tag</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: metaTitle.length > 60 ? 'var(--red)' : metaTitle.length > 50 ? 'var(--amber)' : 'var(--green)' }}>
                    {metaTitle.length}/60 chars {metaTitle.length > 60 ? '⚠ Over limit' : '✓'}
                  </span>
                  <button className="btn btn-sm" onClick={handleRegenTitle} disabled={regenTitle} style={{ fontSize: 11, padding: '3px 10px' }}>
                    {regenTitle ? <><span className="spinner" /> Generating…</> : '↻ Regenerate'}
                  </button>
                </div>
              </div>
              <textarea
                value={metaTitle}
                onChange={e => setMetaTitle(e.target.value)}
                rows={2}
                style={{ fontSize: 13, fontWeight: 500, background: 'transparent', border: 'none', padding: 0, resize: 'none', width: '100%', outline: 'none', color: 'var(--gray-900)' }}
              />
            </div>
            {/* Meta description */}
            <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-md)', padding: '10px 12px', border: `1px solid ${metaDescription.length > 156 ? 'var(--red)' : 'var(--gray-200)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Meta description</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: metaDescription.length > 156 ? 'var(--red)' : metaDescription.length > 140 ? 'var(--amber)' : 'var(--green)' }}>
                    {metaDescription.length}/156 chars {metaDescription.length > 156 ? '⚠ Over limit' : '✓'}
                  </span>
                  <button className="btn btn-sm" onClick={handleRegenDesc} disabled={regenDesc} style={{ fontSize: 11, padding: '3px 10px' }}>
                    {regenDesc ? <><span className="spinner" /> Generating…</> : '↻ Regenerate'}
                  </button>
                </div>
              </div>
              <textarea
                value={metaDescription}
                onChange={e => setMetaDescription(e.target.value)}
                rows={3}
                style={{ fontSize: 13, background: 'transparent', border: 'none', padding: 0, resize: 'none', width: '100%', outline: 'none', color: 'var(--gray-900)' }}
              />
            </div>
          </div>

          {/* Secondary keywords */}
          {brief.secondaryKeywords?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Secondary keywords</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {brief.secondaryKeywords.map((kw, i) => (
                  <span key={i} className="tag">{kw}</span>
                ))}
              </div>
            </div>
          )}

          {/* Page sections */}
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Page structure · Target {brief.wordCountTarget} words
          </div>
          <div style={{ display: 'grid', gap: 6, marginBottom: 16 }}>
            {brief.sections?.map((section, i) => {
              const colors = STAGE_COLORS[section.stage] || STAGE_COLORS['Additional'];
              return (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: colors.bg, border: `1px solid ${colors.border}` }}>
                  <div style={{ flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: colors.border, color: colors.color }}>
                      {section.stage}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.color, marginBottom: 3 }}>
                      {section.element}
                      <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.7 }}>{section.wordCount} words · {section.percentOfPage}</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.color, opacity: 0.85, lineHeight: 1.5 }}>{section.instructions}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* SEO notes */}
          {brief.seoNotes && (
            <div style={{ background: 'var(--blue-light)', border: '1px solid var(--blue-border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SEO notes</div>
              <div style={{ fontSize: 12, color: 'var(--blue)', lineHeight: 1.6 }}>{brief.seoNotes}</div>
            </div>
          )}

          {/* Image ideas */}
          {imageIdeas.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                Image file names & alt text
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {imageIdeas.map((img, i) => (
                  <div key={i} style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--gray-200)', color: 'var(--gray-600)' }}>
                        Image {i + 1}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', flexShrink: 0 }}>File name:</span>
                      <code style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', padding: '1px 6px', borderRadius: 4, wordBreak: 'break-all' }}>{img.fileName}</code>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }} onClick={() => copyToClipboard(img.fileName)}>Copy</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', flexShrink: 0 }}>Alt text:</span>
                      <span style={{ fontSize: 12, color: 'var(--gray-800)', flex: 1 }}>{img.altText}</span>
                      <span style={{ fontSize: 11, color: img.altText.length > 125 ? 'var(--red)' : 'var(--green)', flexShrink: 0 }}>{img.altText.length}/125</span>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }} onClick={() => copyToClipboard(img.altText)}>Copy</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reference URL */}
          <div style={{ marginBottom: 16 }}>
            <label>Reference URL (client's existing page, if any)</label>
            <input
              type="url"
              placeholder="https://clientsite.co.uk/existing-page/"
              value={referenceUrl}
              onChange={e => setReferenceUrl(e.target.value)}
            />
          </div>

          {/* Content editor */}
          {editing && content && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Generated content</div>
                <button className="btn btn-sm" onClick={() => copyToClipboard(content)}>Copy all</button>
              </div>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={20}
                style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7 }}
              />
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleWrite} disabled={writing}>
              {writing
                ? <><span className="spinner" /> Writing content…</>
                : editing ? 'Regenerate content' : 'Write content with AI'}
            </button>
            <button className="btn btn-sm" onClick={() => copyToClipboard(`Title tag:\n${metaTitle}\n\nMeta description:\n${metaDescription}`)}>
              Copy meta data
            </button>
            {editing && content && (
              <button className="btn btn-sm" onClick={handleExportDoc} disabled={exporting}>
                {exporting ? <><span className="spinner" /> Exporting…</> : '↓ Download as Word doc'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function ContentBriefs({ keywords, client, onBack }) {
  const [stage, setStage] = useState('eat'); // 'eat' | 'generating' | 'done'
  const [eatInfo, setEatInfo] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const handleEatSubmit = async (eat) => {
    setEatInfo(eat);
    setStage('generating');
    setProgress({ done: 0, total: keywords.length });

    const results = [];
    for (let i = 0; i < keywords.length; i++) {
      const kw = keywords[i];
      try {
        const result = await generateBrief(
          kw.keyword,
          kw.service,
          kw.location,
          kw.search_volume || kw.searchVolume,
          kw.rank,
          '',
          client.name || client.businessName,
          client.description,
          eat
        );
        results.push({ ...result.brief });
        setBriefs([...results]);
      } catch (e) {
        console.error(`Failed brief for ${kw.keyword}:`, e);
      }
      setProgress({ done: i + 1, total: keywords.length });
    }
    setStage('done');
  };

  if (stage === 'eat') {
    return (
      <EatForm
        client={client}
        onSubmit={handleEatSubmit}
        onBack={onBack}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Back to client</button>
        <div>
          <div className="page-title">Content briefs</div>
          <div className="page-sub">{keywords.length} keyword{keywords.length !== 1 ? 's' : ''} · {client.name || client.businessName}</div>
        </div>
      </div>

      {/* Progress bar while generating */}
      {stage === 'generating' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
            Generating briefs… {progress.done} of {progress.total}
          </div>
          <div style={{ background: 'var(--gray-100)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{
              background: 'var(--blue-mid)', height: '100%',
              width: `${(progress.done / progress.total) * 100}%`,
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      )}

      {briefs.map((brief, i) => (
        <BriefCard key={i} brief={brief} client={client} eatInfo={eatInfo} />
      ))}

      {stage === 'done' && briefs.length > 0 && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button className="btn" onClick={() => { setStage('eat'); setBriefs([]); }}>
            ← Edit client info & regenerate
          </button>
        </div>
      )}
    </div>
  );
}
